import { FC, MouseEvent, useEffect, useState } from 'react';

import {
  Button,
  Menu,
  Stack,
  styled,
  Box,
  Typography,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  Paper,
  OutlinedInput,
} from '@mui/material';

import { IcFilter } from '@/assets/images';
import { NarrowButton, ActiveFilterIndicator } from '@/components';
import { LocalStorageItems } from '@/constants';
import { CFSEventMapper } from '@/models';

const TIMELINE_EVENTS = [
  'CALL_RECEIVED',
  'CALL_ANSWERED',
  'CREATED',
  'UPDATED_INCIDENT_CODES',
  'UNIT_SHIFT_ASSIGNED',
  'UNIT_SHIFT_IS_EN_ROUTE',
  'UNIT_SHIFT_IS_ON_SCENE',
  'UNIT_SHIFT_COMPLETED',
  'UNIT_SHIFT_CANCELED',
  'MARK_FIRE_UNDER_CONTROL',
  'CLOSED',
  'PINNED_LIVE_INFO',
  'CUSTOM_EVENT',
];

interface ITimelineFilterMenuProps {
  onChangeFilter: (value: string[]) => void;
}

const FilterContainer = styled(Paper)(() => ({
  padding: 16,
  width: 300,
  boxShadow: 'none',
}));

export const TimelineFilterMenu: FC<ITimelineFilterMenuProps> = ({
  onChangeFilter,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [showFilterDot, setShowFilterDot] = useState(false);

  const openMenu = Boolean(anchorEl);

  useEffect(() => {
    const saved = localStorage.getItem(LocalStorageItems.TimelineFilterEvents);
    if (saved) {
      const parsed = JSON.parse(saved);
      setSelectedEvents(parsed);
      setShowFilterDot(parsed.length > 0);
      onChangeFilter(parsed);
    }
  }, []);

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setSelectedEvents(typeof value === 'string' ? value.split(',') : value);
  };

  const onClear = () => {
    setSelectedEvents([]);
  };

  const onApply = () => {
    onChangeFilter(selectedEvents);
    setShowFilterDot(selectedEvents.length > 0);
    localStorage.setItem(
      LocalStorageItems.TimelineFilterEvents,
      JSON.stringify(selectedEvents),
    );
    handleCloseMenu();
  };

  const getEventLabel = (event: string): string => {
    return CFSEventMapper[event as keyof typeof CFSEventMapper] || event;
  };

  return (
    <>
      <NarrowButton
        variant="contained"
        color="inherit"
        onClick={handleOpenMenu}
        sx={{ position: 'relative' }}
      >
        <IcFilter />
        {showFilterDot && <ActiveFilterIndicator />}
      </NarrowButton>

      <Menu
        id="timeline-filter-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          disablePadding: true,
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <FilterContainer>
          <Typography fontWeight={500} sx={{ mb: 2 }}>
            Timeline Status
          </Typography>

          <FormControl fullWidth>
            <Select
              multiple
              value={selectedEvents}
              onChange={handleChange}
              input={<OutlinedInput />}
              displayEmpty
              renderValue={(selected) => {
                if (selected.length === 0) return 'Select status';
                if (selected.length === TIMELINE_EVENTS.length) return 'All';
                return `${selected.length} selected`;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 280,
                  },
                },
              }}
            >
              {TIMELINE_EVENTS.map((event) => (
                <MenuItem key={event} value={event} dense>
                  <Checkbox
                    checked={selectedEvents.includes(event)}
                    size="small"
                  />
                  <ListItemText
                    primary={getEventLabel(event)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      style: { fontWeight: 400 },
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ my: 2, height: '1px', bgcolor: 'divider' }} />

          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Button
              size="large"
              color="inherit"
              onClick={onClear}
              sx={{ flex: 1 }}
            >
              Clear
            </Button>
            <Button
              size="large"
              variant="contained"
              color="error"
              onClick={onApply}
              sx={{ flex: 1 }}
            >
              Apply
            </Button>
          </Stack>
        </FilterContainer>
      </Menu>
    </>
  );
};
